import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import axios from "axios";
import { Link } from "react-router-dom";
import { UserContext } from "../ProfileLayout";

const API_URL = `${process.env.REACT_APP_API_BASE_URL}/user/user-orders`;

const Card = styled.div`
    background: #fff;
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    flex: 1;
    overflow-x: auto;

    @media (max-width: 480px) {
        padding: 16px;
    }
`;

const PageTitle = styled.h3`
    margin-bottom: 16px;
    font-size: 18px;
    font-weight: 600;
`;

// Десктопная таблица
const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;

    th, td {
        padding: 10px 8px;
        text-align: left;
    }

    thead {
        background: #f5f7fa;

        th {
            font-weight: 600;
            color: #555;
        }
    }

    tr + tr {
        border-top: 1px solid #eee;
    }

    .green {
        color: #2ecc71;
        font-weight: 600;
    }

    .orange {
        color: #e67e22;
        font-weight: 600;
    }

    .red {
        color: #e74c3c;
        font-weight: 600;
    }

    .blue {
        color: #3498db;
        font-weight: 600;
    }

    @media (max-width: 768px) {
        display: none;
    }
`;

// Мобильный список карточек
const MobileOrdersList = styled.div`
    display: none;
    
    @media (max-width: 768px) {
        display: block;
    }
`;

const MobileOrderCard = styled.div`
    background: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 12px;
    border: 1px solid #e8ecef;
`;

const MobileOrderHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
`;

const MobileOrderId = styled.div`
    font-weight: 600;
    font-size: 15px;
    color: #333;
`;

const MobileStatusBadge = styled.span`
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;

    &.green {
        background: #d4edda;
        color: #155724;
    }

    &.orange {
        background: #fff3cd;
        color: #856404;
    }

    &.red {
        background: #f8d7da;
        color: #721c24;
    }

    &.blue {
        background: #e7f3ff;
        color: #0066cc;
    }
`;

const MobileOrderInfo = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-bottom: 12px;
    
    @media (max-width: 480px) {
        grid-template-columns: 1fr;
        gap: 6px;
    }
`;

const MobileOrderField = styled.div`
    font-size: 12px;
    
    .label {
        color: #666;
        font-weight: 500;
        display: block;
        margin-bottom: 2px;
        text-transform: uppercase;
        font-size: 10px;
        letter-spacing: 0.5px;
    }
    
    .value {
        color: #333;
        font-weight: 400;
        font-size: 13px;
    }
`;

const MobileOrderAction = styled.div`
    text-align: right;
    
    a {
        color: #1B6392;
        text-decoration: none;
        font-size: 12px;
        font-weight: 500;
        
        &:hover {
            text-decoration: underline;
        }
    }
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 40px 20px;
    color: #666;
    font-size: 14px;

    @media (max-width: 768px) {
        padding: 20px;
    }
`;

const ActionLink = styled(Link)`
    color: #1B6392;
    text-decoration: none;
    font-weight: 500;

    &:hover {
        text-decoration: underline;
    }
`;

export default function OrderHistoryPage() {
    const userInfo = useContext(UserContext);
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        axios.get(API_URL, {
            headers: { Authorization: `Bearer ${sessionStorage.getItem("accessToken")}` }
        })
            .then(({ data }) => setOrders(data || []))
            .catch(console.error);
    }, []);

    const statusClass = status => {
        if (/ЗАВЕРШ|COMPLETED|DONE/i.test(status)) return "green";
        if (/ПРОЦ|PROCESS|IN_PROGRESS|PENDING/i.test(status)) return "orange";
        if (/ОТМ|CANCEL/i.test(status)) return "red";
        if (/WAITING_CONFIRMATION/i.test(status)) return "blue";
        return "";
    };

    const getStatusText = status => {
        if (status === "WAITING_CONFIRMATION") return "ОЖИДАНИЕ";
        if (status === "PENDING") return "В ОБРАБОТКЕ";
        if (status === "COMPLETED") return "ЗАВЕРШЕН";
        if (status === "CANCELLED") return "ОТМЕНЕН";
        return status;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString("ru", {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Card>
            <PageTitle>История заказов</PageTitle>

            {/* Десктопная таблица */}
            <Table>
                <thead>
                <tr>
                    <th>Идентификатор заказа</th>
                    <th>Статус</th>
                    <th>Дата заказа</th>
                    <th>Всего</th>
                    <th>Действие</th>
                </tr>
                </thead>
                <tbody>
                {orders.length === 0 && (
                    <tr>
                        <td colSpan={5}>
                            <EmptyState>Заказы не найдены</EmptyState>
                        </td>
                    </tr>
                )}
                {orders.map(o => (
                    <tr key={o.orderId}>
                        <td>#{o.orderId}</td>
                        <td className={statusClass(o.payStatus)}>{getStatusText(o.payStatus)}</td>
                        <td>{formatDate(o.orderDate)}</td>
                        <td>{o.totalPrice}</td>
                        <td>
                            <ActionLink to={`/profile/orders/${o.orderId}`}>
                                Посмотреть детали →
                            </ActionLink>
                        </td>
                    </tr>
                ))}
                </tbody>
            </Table>

            {/* Мобильные карточки */}
            <MobileOrdersList>
                {orders.length === 0 ? (
                    <EmptyState>Заказы не найдены</EmptyState>
                ) : (
                    orders.map(o => (
                        <MobileOrderCard key={o.orderId}>
                            <MobileOrderHeader>
                                <MobileOrderId>#{o.orderId}</MobileOrderId>
                                <MobileStatusBadge className={statusClass(o.payStatus)}>
                                    {getStatusText(o.payStatus)}
                                </MobileStatusBadge>
                            </MobileOrderHeader>

                            <MobileOrderInfo>
                                <MobileOrderField>
                                    <span className="label">Дата заказа</span>
                                    <span className="value">{formatDate(o.orderDate)}</span>
                                </MobileOrderField>
                                <MobileOrderField>
                                    <span className="label">Всего</span>
                                    <span className="value">{o.totalPrice}</span>
                                </MobileOrderField>
                            </MobileOrderInfo>

                            <MobileOrderAction>
                                <ActionLink to={`/profile/orders/${o.orderId}`}>
                                    Посмотреть детали →
                                </ActionLink>
                            </MobileOrderAction>
                        </MobileOrderCard>
                    ))
                )}
            </MobileOrdersList>
        </Card>
    );
}
